/**
 * Authentication API Client
 *
 * Provides functions to interact with authentication endpoints:
 * - Login
 * - Logout
 * - Refresh token
 * - Get current user info
 */

import apiClient from './client';
import type {
  LoginRequest,
  LoginResponse,
  RefreshTokenResponse,
  User,
  ChangePasswordRequest,
  ChangePasswordResponse,
} from '@/types/auth';
import { setTokens, removeTokens, getUserInfo } from '@/utils/token';

/**
 * Login with username and password (V107)
 * For regular users: ORG_ADMIN, ORG_POWER, ORG_USER
 *
 * @param credentials - Username and password
 * @returns Login response with JWT tokens and user info
 */
export const login = async (credentials: LoginRequest): Promise<LoginResponse> => {
  const response = await apiClient.post<LoginResponse>('/api/auth/login', credentials);

  const { accessToken, refreshToken, username, name, email, roles, organization } = response.data;

  // Store tokens and user info in localStorage (V107: username-based)
  setTokens(accessToken, refreshToken, username, name, email, roles, organization);

  // V1173: 로그인 성공 시 FORBIDDEN_ORG 카운터 리셋
  sessionStorage.removeItem('_fo_count');

  return response.data;
};

/**
 * Admin login with username and password (V108)
 * For system administrators: SUPER_ADMIN, ADMIN
 *
 * @param credentials - Username and password
 * @returns Login response with JWT tokens and user info
 */
export const adminLogin = async (credentials: LoginRequest): Promise<LoginResponse> => {
  const response = await apiClient.post<LoginResponse>('/api/auth/admin/login', credentials);

  const { accessToken, refreshToken, username, name, email, roles, organization } = response.data;

  // Store tokens and user info in localStorage (V108: admin login)
  setTokens(accessToken, refreshToken, username, name, email, roles, organization);

  // V1173: 로그인 성공 시 FORBIDDEN_ORG 카운터 리셋
  sessionStorage.removeItem('_fo_count');

  return response.data;
};

/**
 * Logout and clear all tokens
 * JWT is stateless, so we only need to clear client-side tokens
 */
export const logout = async (): Promise<void> => {
  // JWT stateless logout - simply remove tokens from localStorage
  removeTokens();
};

/**
 * Refresh access token using refresh token
 *
 * @param refreshToken - Refresh token
 * @returns New access token
 */
export const refreshAccessToken = async (
  refreshToken: string
): Promise<RefreshTokenResponse> => {
  const response = await apiClient.post<RefreshTokenResponse>(
    '/api/auth/refresh',
    { refreshToken }
  );
  return response.data;
};

/**
 * Get current user information from localStorage (V107: username-based)
 *
 * @returns User info or null if not authenticated
 */
export const getCurrentUser = (): User | null => {
  const userInfo = getUserInfo();

  if (!userInfo.username || !userInfo.name) {
    return null;
  }

  return {
    username: userInfo.username,
    name: userInfo.name,
    email: userInfo.email || '',
    roles: userInfo.roles,
    organization: userInfo.organization,
  };
};

/**
 * Get current user information from backend (verify token)
 *
 * @returns User info from backend
 */
export const fetchCurrentUser = async (): Promise<User> => {
  const response = await apiClient.get<User>('/api/users/me');
  return response.data;
};

/**
 * Check if user is authenticated (V107: username-based)
 *
 * @returns True if access token exists in localStorage
 */
export const checkAuth = (): boolean => {
  const userInfo = getUserInfo();
  return !!userInfo.username && !!userInfo.name;
};

/**
 * Change user password
 *
 * @param request - Current password and new password
 * @returns Success message from backend
 */
export const changePassword = async (
  request: ChangePasswordRequest
): Promise<ChangePasswordResponse> => {
  const response = await apiClient.post<ChangePasswordResponse>(
    '/api/auth/password/change',
    request
  );
  return response.data;
};
